import React, { useEffect, useState } from 'react';
import { Navbar } from '../layout/Navbar';
import { Footer } from '../layout/Footer';
import { useStore, Member } from '../store/usestore';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '../ui/Button';
import { Home } from 'lucide-react';

export const ActiveMembersPage = () => {
  const { members, fetchData, hasHydrated } = useStore();

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (!hasHydrated) return null;

  return (
    <div className="min-h-screen bg-white font-sans text-gray-900">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="mb-8 flex justify-end">
            <Link to="/">
              <Button variant="outline" size="sm" className="flex items-center gap-2">
                <Home className="w-4 h-4" />
              </Button>
            </Link>
          </div>
          <div className="text-center mb-12">
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4">NWC Members</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Our strength lies in our people. Meet the full team dedicated to our grassroots mobilization efforts.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6">
            {members.map((member) => (
              <MemberCard key={member.id} member={member} />
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

const MemberCard = ({ member }: { member: Member }) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      whileHover={{ y: -5 }}
      className="bg-white border border-gray-200 rounded-xl p-6 flex flex-col items-center text-center shadow-sm hover:shadow-md transition-all hover:border-primary"
    >
      <div className="w-full aspect-square rounded-lg overflow-hidden mb-4 bg-gray-100">
        <motion.img 
          src={member.image} 
          alt={member.name} 
          className="w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: imageLoaded ? 1 : 0 }}
          transition={{ duration: 0.5 }}
          onLoad={() => setImageLoaded(true)}
          loading="lazy"
          decoding="async"
        />
      </div>
      <h3 className="text-lg font-bold text-gray-900">{member.name}</h3>
      {member.title && <p className="text-sm font-medium text-primary">{member.title}</p>}
      <p className="text-sm text-gray-500">{member.role}</p>
    </motion.div>
  );
};
